import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { db } from "@/api/db";
import { Plus, Edit, Trash2, ArrowLeft, Play, Calendar, Eye, EyeOff, LayoutList, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminLessons() {
  const urlParams = new URLSearchParams(window.location.search);
  const trackId = urlParams.get("track_id");

  const [track, setTrack] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("lesson"); // "lesson" | "section"
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type: "recorded", video_url: "", live_url: "", live_date: "", duration_minutes: "", order: 1, is_published: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (trackId) loadData(); }, [trackId]);

  async function loadData() {
    const [tracks, lsns] = await Promise.all([
      db.entities.CareerTrack.filter({ id: trackId }),
      db.entities.Lesson.filter({ track_id: trackId }, "order"),
    ]);
    setTrack(tracks[0] || null);
    setLessons(lsns);
    setLoading(false);
  }

  function openCreateLesson() {
    setEditing(null);
    setFormMode("lesson");
    setForm({ title: "", description: "", type: "recorded", video_url: "", live_url: "", live_date: "", duration_minutes: "", order: lessons.length + 1, is_published: false });
    setShowForm(true);
  }

  function openCreateSection() {
    setEditing(null);
    setFormMode("section");
    setForm({ title: "", description: "", type: "section", video_url: "", live_url: "", live_date: "", duration_minutes: "", order: lessons.length + 1, is_published: true });
    setShowForm(true);
  }

  function openEdit(l) {
    setEditing(l);
    setFormMode(l.type === "section" ? "section" : "lesson");
    setForm({
      title: l.title || "", description: l.description || "", type: l.type || "recorded",
      video_url: l.video_url || "", live_url: l.live_url || "",
      live_date: l.live_date ? l.live_date.slice(0, 16) : "",
      duration_minutes: l.duration_minutes || "", order: l.order || 1,
      is_published: !!l.is_published,
    });
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    const data = {
      ...form, track_id: trackId,
      order: Number(form.order),
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      live_date: form.live_date ? new Date(form.live_date).toISOString() : null,
    };
    if (formMode === "section") {
      data.type = "section";
      data.video_url = "";
      data.live_url = "";
      data.live_date = null;
      data.duration_minutes = null;
    }
    if (editing) {
      await db.entities.Lesson.update(editing.id, data);
    } else {
      await db.entities.Lesson.create(data);
    }
    await loadData();
    setShowForm(false);
    setSaving(false);
  }

  async function moveItem(item, direction) {
    const idx = lessons.findIndex(i => i.id === item.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= lessons.length) return;
    const other = lessons[swapIdx];
    await Promise.all([
      db.entities.Lesson.update(item.id, { order: other.order ?? swapIdx + 1 }),
      db.entities.Lesson.update(other.id, { order: item.order ?? idx + 1 }),
    ]);
    await loadData();
  }

  async function togglePublish(l) {
    await db.entities.Lesson.update(l.id, { is_published: !l.is_published });
    await loadData();
  }

  async function deleteLesson(l) {
    const label = l.type === "section" ? "seção" : "aula";
    if (!window.confirm(`Excluir ${label} "${l.title}"?`)) return;
    await db.entities.Lesson.delete(l.id);
    await loadData();
  }

  const lessonCount = lessons.filter(l => l.type !== "section").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Link to={createPageUrl("AdminTracks")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar aos módulos
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Aulas — {track?.title}</h1>
            <p className="text-gray-500 mt-1">{lessonCount} aula(s)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openCreateSection} variant="outline" className="gap-2">
              <LayoutList className="w-4 h-4" /> Nova Seção
            </Button>
            <Button onClick={openCreateLesson} className="bg-emerald-500 hover:bg-emerald-600 gap-2">
              <Plus className="w-4 h-4" /> Nova Aula
            </Button>
          </div>
        </div>
      </div>

      {lessons.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-16 text-center text-gray-400">
            <Play className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhuma aula criada ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {lessons.map((l, idx) => l.type === "section" ? (
            <div key={l.id} className="flex items-center gap-2 pt-4 pb-1">
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <Button size="sm" variant="ghost" className="h-5 w-5 p-0" disabled={idx === 0}
                  onClick={() => moveItem(l, -1)}>
                  <ChevronUp className="w-3 h-3 text-gray-400" />
                </Button>
                <Button size="sm" variant="ghost" className="h-5 w-5 p-0" disabled={idx === lessons.length - 1}
                  onClick={() => moveItem(l, 1)}>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <LayoutList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <p className="font-bold text-gray-700 text-sm uppercase tracking-wide truncate">{l.title}</p>
                {l.description && (
                  <span className="text-xs text-gray-400 truncate hidden sm:inline">— {l.description}</span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(l)}>
                  <Edit className="w-3.5 h-3.5 text-gray-400" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => deleteLesson(l)}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              </div>
            </div>
          ) : (
            <Card key={l.id} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <Button size="sm" variant="ghost" className="h-5 w-5 p-0" disabled={idx === 0}
                    onClick={() => moveItem(l, -1)}>
                    <ChevronUp className="w-3 h-3 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-5 w-5 p-0" disabled={idx === lessons.length - 1}
                    onClick={() => moveItem(l, 1)}>
                    <ChevronDown className="w-3 h-3 text-gray-400" />
                  </Button>
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-50">
                  {l.type === "live" ? <Calendar className="w-4 h-4 text-rose-500" /> : <Play className="w-4 h-4 text-emerald-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{l.order}. {l.title}</p>
                    <Badge variant="outline" className="text-xs">{l.type === "live" ? "Live" : "Gravada"}</Badge>
                    <Badge className={`text-xs border-0 ${l.is_published ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                      {l.is_published ? "Pub." : "Rascunho"}
                    </Badge>
                  </div>
                  {l.type === "live" && l.live_date && (
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(l.live_date).toLocaleString("pt-BR")}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => togglePublish(l)}>
                    {l.is_published ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(l)}>
                    <Edit className="w-4 h-4 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => deleteLesson(l)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formMode === "section"
                ? (editing ? "Editar Seção" : "Nova Seção")
                : (editing ? "Editar Aula" : "Nova Aula")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Título *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder={formMode === "section" ? "Ex: Introdução" : ""} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Descrição</label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 resize-none" rows={2} />
            </div>

            {formMode === "lesson" && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700">Tipo</label>
                  <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recorded">Aula Gravada</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.type === "recorded" && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">URL do Vídeo</label>
                    <Input value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="https://youtube.com/..." className="mt-1" />
                  </div>
                )}
                {form.type === "live" && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700">URL da Live</label>
                      <Input value={form.live_url} onChange={e => setForm(f => ({ ...f, live_url: e.target.value }))} placeholder="https://meet.google.com/..." className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Data e Hora</label>
                      <Input type="datetime-local" value={form.live_date} onChange={e => setForm(f => ({ ...f, live_date: e.target.value }))} className="mt-1" />
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Ordem</label>
                    <Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Duração (min)</label>
                    <Input type="number" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} className="mt-1" />
                  </div>
                </div>
              </>
            )}

            {formMode === "section" && (
              <div className="w-24">
                <label className="text-sm font-medium text-gray-700">Ordem</label>
                <Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleSave} disabled={!form.title || saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
